import { AppManager } from 'core';
import { action, observable } from 'mobx';

import {
  InitiativeQuery,
  ProfileQuery,
  ProfileCardsQuery,
  InitiativeCardsQuery,
} from '../gql';

export type CreditCard = {
  id: string;
  brand: string;
  country?: string;
  expMonth: number;
  expYear: number;
  lastDigits: string;
  isDefault: boolean;
  name?: string;
};

export type Group = {
  profile: { email?: string; username: string };
};

export class UpgradeManager extends AppManager {
  @observable initiativeQuery: InitiativeQuery;
  @observable profileQuery: ProfileQuery;

  @observable activePlan: string;

  @observable billingCycle: string = 'monthly';

  @observable purchasedSeats: number = 0;
  @observable includedPlanSeats: number = 0;
  @observable freeSeats: number = 0;
  @observable unusedSeats: number = 0;
  @observable isEarlyAccess: boolean;
  @observable loading: boolean;
  @observable hasBillSubscription: boolean;
  @observable currentPeriodEnd: Date;

  @observable teamName: string = '';
  @observable creditCards: CreditCard[] = [];

  @observable groups: Group[];

  @observable collaborators: { username: string }[] = [];

  @action QueryInitiative = (slug: string) => {
    this.initiativeQuery = new InitiativeQuery(this, {
      variables: {
        slug,
      },
    });
    this.loading = true;
    this.initiativeQuery.process(
      (initiative: any) => {
        if (!initiative) {
          return;
        }

        this.creditCards = initiative.creditCards;
        this.teamName = initiative.content.title;

        if (initiative.billingSubscriptions?.length) {
          this.currentPeriodEnd =
            initiative.billingSubscriptions[0]?.currentPeriodEnd;
          const subscription =
            initiative.billingSubscriptions[0]?.plan?.name?.split(' ');
          // get the billing cycle
          if (subscription) {
            const billingCycle = subscription[1];
            const productType =
              initiative.billingSubscriptions[0]?.plan?.product?.name;

            this.billingCycle = billingCycle;

            this.activePlan = productType.toLowerCase();

            this.activePlan = this.activePlan === 'team' ? 'pro' : 'business';
          }
        } else {
          this.billingCycle = 'monthly';
        }

        this.purchasedSeats = initiative.seatsUsage.purchasedSeats;
        this.includedPlanSeats = initiative.seatsUsage.includedPlanSeats;
        this.freeSeats = initiative.seatsUsage.freeSeats;
        this.unusedSeats = initiative.seatsUsage.unusedSeats;
        this.isEarlyAccess = initiative.isEarlyAccess;
        this.hasBillSubscription = !!initiative.billingSubscriptions?.length;

        this.collaborators = initiative.collaborators;

        this.groups = initiative.groups;
        this.loading = false;
      },
      null,
      { subPath: 'initiative' },
    );

    this.initiativeQuery.query();
  };

  @action QueryProfile = (username: string) => {
    this.profileQuery = new ProfileQuery(this, {
      variables: {
        username,
      },
    });
    this.loading = true;
    this.profileQuery.process(
      (profile: any) => {
        if (!profile) {
          return;
        }

        this.creditCards = profile.creditCards;

        this.billingCycle = 'monthly';

        this.groups = [];
        this.loading = false;
      },
      null,
      { subPath: 'profile' },
    );

    this.profileQuery.query();
  };

  @action QueryProfileCreditCards = (username: string) => {
    const profileCreditCardsQuery = new ProfileCardsQuery(this, {
      variables: {
        username,
      },
    });
    profileCreditCardsQuery.process(
      (profile: any) => {
        if (!profile) {
          return;
        }

        this.creditCards = profile.creditCards;
      },
      null,
      { subPath: 'profile' },
    );

    profileCreditCardsQuery.query();
  };

  @action QueryInitiativeCreditCards = (slug: string) => {
    const initiativeCreditCardsQuery = new InitiativeCardsQuery(this, {
      variables: {
        slug,
      },
      fetchPolicy: 'network-only',
    });
    initiativeCreditCardsQuery.process(
      (initiative: any) => {
        if (!initiative) {
          return;
        }

        this.creditCards = initiative.creditCards;
      },
      null,
      { subPath: 'initiative' },
    );

    initiativeCreditCardsQuery.query();
  };
}
